import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Alert, Spinner } from '../../components/ui';
import { ErrorPanel } from '../../components/ErrorPanel';
import { BASE, SrBuildingMe, keys, useBuildingRole } from './api';

/** Pages only a building administrator may open. The menu already hides them; the server refuses anyone else. */
export function AdminOnly({ children }: { children: ReactNode }) {
  const role = useBuildingRole();
  if (role !== 'building_admin') {
    return (
      <div className="mx-auto max-w-xl py-10">
        <Alert kind="info">{t('srb.adminOnly')}</Alert>
      </div>
    );
  }
  return <>{children}</>;
}

/** The building's details and settings (any role may read them). */
export function useBuildingMe() {
  return useQuery({ queryKey: keys.me, queryFn: () => api.get(`${BASE}/me`, { schema: SrBuildingMe }) });
}

/** Loads the building before drawing a page that needs its settings. */
export function WithBuilding({ children }: { children: (me: SrBuildingMe) => ReactNode }) {
  const q = useBuildingMe();
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  return <>{children(q.data)}</>;
}
